import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FiSend, FiMic, FiPenTool, FiVolume2, FiVolumeX } from 'react-icons/fi'
import { useLocation } from 'react-router-dom'

import LyoAvatar from '../components/avatar/LyoAvatar'
import ModeSelector from '../components/chat/ModeSelector'
import VoiceChatModal from '../components/chat/VoiceChatModal'
import { useMood } from '../contexts/MoodContext'
import { useChat } from '../contexts/ChatContext'
import { useVoiceChat } from '../hooks/useVoiceChat'
import { useSettings } from '../contexts/SettingsContext'

const Chat = () => {
  const { mood } = useMood()
  const { messages, sendMessage, mode, setMode, error } = useChat()
  const { speak, stopAllVoiceActivities, isSpeaking, stopSpeaking } = useVoiceChat()
  const { settings } = useSettings()
  const [input, setInput] = useState('')
  const [showVoiceModal, setShowVoiceModal] = useState(false)
  const [lyoEmotion, setLyoEmotion] = useState('neutral')
  const [playingMessageId, setPlayingMessageId] = useState(null)
  const [lastProcessedMessageId, setLastProcessedMessageId] = useState(null)
  const messagesEndRef = useRef(null)
  const textareaRef = useRef(null)
  const chatContainerRef = useRef(null)
  const location = useLocation()
  const hasNavigatedAwayRef = useRef(false)
  const isInitialLoadRef = useRef(true)
  
  // Track when user navigates away from chat
  useEffect(() => {
    if (location.pathname !== '/chat') {
      console.log('🧭 User navigated away from chat to:', location.pathname)
      hasNavigatedAwayRef.current = true
      
      // Stop any ongoing speech when navigating away
      stopAllVoiceActivities()
    }
  }, [location.pathname, stopAllVoiceActivities])
  
  // Handle initial load and navigation back to chat
  useEffect(() => {
    if (location.pathname === '/chat') {
      if (isInitialLoadRef.current) {
        // First time loading chat page
        console.log('🏠 Initial chat page load')
        isInitialLoadRef.current = false
        hasNavigatedAwayRef.current = false
        
        // Set the last processed message to prevent auto-speaking existing messages
        if (messages.length > 0) {
          const latestMessage = messages[messages.length - 1]
          setLastProcessedMessageId(latestMessage.id)
          console.log('Set initial lastProcessedMessageId to:', latestMessage.id)
        }
      } else if (hasNavigatedAwayRef.current) {
        // Returning to chat after navigation
        console.log('🏠 User returned to chat - preventing auto-speak of existing messages')
        
        // Immediately set the last processed message to the latest one
        if (messages.length > 0) {
          const latestMessage = messages[messages.length - 1]
          setLastProcessedMessageId(latestMessage.id)
          console.log('Updated lastProcessedMessageId on return to:', latestMessage.id)
        }
        
        // Reset navigation flag after a delay
        const timer = setTimeout(() => {
          console.log('✅ Navigation flag reset - new messages will auto-speak')
          hasNavigatedAwayRef.current = false
        }, 1000)
        
        return () => clearTimeout(timer)
      }
    }
  }, [location.pathname, messages])
  
  // Stop all voice activities when leaving the chat page
  useEffect(() => {
    return () => {
      console.log('🚪 Leaving Chat page - stopping all voice activities')
      stopAllVoiceActivities()
    }
  }, [stopAllVoiceActivities])
  
  const handleSendMessage = (e) => {
    e.preventDefault()
    if (input.trim()) {
      const messageText = input.trim()
      
      // Clear input immediately for instant feedback
      setInput('')
      
      // Reset textarea height immediately
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
      
      // Set emotion and send message (don't await)
      setLyoEmotion('thinking')
      sendMessage(messageText)
      
      // Clear navigation flag since user is actively sending new messages
      hasNavigatedAwayRef.current = false
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage(e)
    }
  }
  
  const handleVoiceMessage = (voiceText) => {
    if (voiceText) {
      // Set emotion and send message (don't await)
      setLyoEmotion('thinking')
      sendMessage(voiceText)
      
      // Clear navigation flag since user is actively sending new messages
      hasNavigatedAwayRef.current = false
    }
    setShowVoiceModal(false)
  }

  // Handle audio replay for individual messages
  const handleAudioReplay = async (messageText, messageId) => {
    console.log('🔄 Replaying audio for message:', messageId)
    
    // If currently playing this message, stop it
    if (playingMessageId === messageId && isSpeaking) {
      console.log('Stopping current playback')
      stopSpeaking()
      setPlayingMessageId(null)
      return
    }
    
    // Stop any other playing audio first
    if (isSpeaking) {
      console.log('Stopping other audio before replay')
      stopSpeaking()
    }
    
    // Set this message as playing
    setPlayingMessageId(messageId)
    
    try {
      // Create a custom speak function that tracks completion for this specific message
      await speakWithTracking(messageText, messageId)
    } catch (error) {
      console.error('Error replaying audio:', error)
      setPlayingMessageId(null)
    }
  }

  // Custom speak function with completion tracking
  const speakWithTracking = async (text, messageId) => {
    return new Promise((resolve, reject) => {
      // Create a temporary audio element to track completion
      const originalSpeak = speak
      
      // Override the speak function temporarily to track this specific message
      const trackableSpeak = async (text) => {
        try {
          await originalSpeak(text)
          
          // Check periodically if speaking has stopped
          const checkCompletion = () => {
            if (!isSpeaking) {
              console.log('✅ Audio replay completed for message:', messageId)
              setPlayingMessageId(null)
              resolve()
            } else {
              setTimeout(checkCompletion, 100)
            }
          }
          
          // Start checking after a brief delay
          setTimeout(checkCompletion, 500)
          
        } catch (error) {
          console.error('Error in trackable speak:', error)
          setPlayingMessageId(null)
          reject(error)
        }
      }
      
      trackableSpeak(text)
    })
  }

  // Reset playing state when speaking stops
  useEffect(() => {
    if (!isSpeaking && playingMessageId) {
      console.log('Speaking stopped, clearing playing message ID')
      setPlayingMessageId(null)
    }
  }, [isSpeaking, playingMessageId])

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textareaRef.current.style.height = 'auto'
      
      // Calculate the new height based on content
      const scrollHeight = textareaRef.current.scrollHeight
      const maxHeight = 200 // Maximum height in pixels (about 8-10 lines)
      const minHeight = 40 // Minimum height in pixels
      
      // Set the height, but don't exceed maxHeight
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight)
      textareaRef.current.style.height = `${newHeight}px`
      
      // Enable scrolling if content exceeds maxHeight
      if (scrollHeight > maxHeight) {
        textareaRef.current.style.overflowY = 'auto'
      } else {
        textareaRef.current.style.overflowY = 'hidden'
      }
    }
  }
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    adjustTextareaHeight()
  }, [input])

  // Auto-speak NEW Lyo messages ONLY (with strict conditions)
  useEffect(() => {
    // Only process if we're on the chat page, not in voice modal, and have messages
    if (location.pathname === '/chat' && !showVoiceModal && messages.length > 0) {
      // Get the latest message
      const latestMessage = messages[messages.length - 1]
      
      // STRICT CONDITIONS for auto-speaking:
      // 1. Must be a Lyo message
      // 2. Must not have been processed before
      // 3. Voice must be enabled
      // 4. Must not have navigated away recently
      // 5. Must not be currently speaking
      // 6. Must not be initial load
      if (latestMessage && 
          latestMessage.sender === 'lyo' && 
          latestMessage.id !== lastProcessedMessageId &&
          settings.voiceEnabled && 
          !hasNavigatedAwayRef.current &&
          !isSpeaking &&
          !isInitialLoadRef.current) {
        
        console.log('🗣️ Auto-speaking NEW Lyo message:', latestMessage.text)
        setLyoEmotion('happy')
        setPlayingMessageId(latestMessage.id)
        setLastProcessedMessageId(latestMessage.id)
        
        // Speak the message
        speak(latestMessage.text)
        
        // Reset emotion after speaking
        setTimeout(() => {
          setLyoEmotion('neutral')
        }, 3000)
      } else if (latestMessage && latestMessage.sender === 'lyo' && latestMessage.id !== lastProcessedMessageId) {
        // Just update emotion and mark as processed if voice conditions not met
        console.log('New Lyo message received - updating emotion only (conditions not met for auto-speak)')
        setLyoEmotion('happy')
        setLastProcessedMessageId(latestMessage.id)
        setTimeout(() => {
          setLyoEmotion('neutral')
        }, 3000)
      }
    }
  }, [messages, speak, location.pathname, showVoiceModal, isSpeaking, settings.voiceEnabled, lastProcessedMessageId])

  // Reset processed message ID when messages are cleared
  useEffect(() => {
    if (messages.length === 0) {
      setLastProcessedMessageId(null)
    }
  }, [messages.length])

  const examples = [
    { text: "How are you feeling today?" },
    { text: "I need some clarity about a situation" },
    { text: "Can you help me process my thoughts?" },
    { text: "I'm feeling overwhelmed" }
  ]
  
  return (
    <motion.div 
      className="min-h-screen pt-16 pb-6 px-4 flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-4xl mx-auto w-full flex-1 flex flex-col">
        {error && (
          <div className="mb-4 p-4 bg-error-dark/50 text-error-light rounded-lg text-center">
            <p className="font-medium">{error}</p>
            <p className="text-sm mt-2">Please add your OpenAI API key to the environment variables to enable chat functionality.</p>
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6 glass-panel p-4 rounded-2xl">
          <div className="flex items-center">
            <div className="ml-4">
              <h2 className="text-xl font-display text-white">Lyo</h2>
              <p className="text-gray-400 text-sm">Here to help you reflect</p>
            </div>
          </div>
          
          <ModeSelector currentMode={mode} onChange={setMode} />
        </div>
        
        <div 
          ref={chatContainerRef}
          className="flex-1 glass-panel rounded-2xl p-4 mb-4 overflow-y-auto"
        >
          <div className="flex flex-col space-y-4">
            {messages.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <p className="text-gray-400 mb-4">Start chatting with Lyo...</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {examples.map((example, index) => (
                    <button 
                      key={index}
                      onClick={() => setInput(example.text)}
                      className="bg-glass-light px-4 py-2 rounded-full text-sm text-gray-300 hover:bg-glass-medium transition-all"
                    >
                      {example.text}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <AnimatePresence>
                {messages.map((message, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`chat-bubble ${
                      message.sender === 'user' ? 'chat-bubble-user' : 'chat-bubble-lyo'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {message.sender === 'user' && (
                        <div className="flex-shrink-0 w-8 h-8 bg-neon-pink/20 rounded-full flex items-center justify-center">
                          <span className="text-neon-pink text-sm font-medium">You</span>
                        </div>
                      )}
                      {message.sender === 'lyo' && (
                        <div className="flex-shrink-0 w-8 h-8 bg-neon-cyan/20 rounded-full flex items-center justify-center">
                          <span className="text-neon-cyan text-sm font-medium">Lyo</span>
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="whitespace-pre-wrap">{message.text}</p>
                        {message.sender === 'lyo' && (
                          <div className="flex justify-between items-center mt-3">
                            <button 
                              className="text-xs text-gray-400 hover:text-neon-cyan flex items-center transition-colors"
                              onClick={() => {/* Add to memory functionality */}}
                            >
                              <FiPenTool className="mr-1" /> Save to memory
                            </button>
                            
                            {/* Audio Replay Button */}
                            <button
                              onClick={() => handleAudioReplay(message.text, message.id)}
                              className={`flex items-center text-xs transition-all duration-200 ${
                                playingMessageId === message.id && isSpeaking
                                  ? 'text-neon-cyan hover:text-neon-cyan/80'
                                  : 'text-gray-400 hover:text-neon-cyan'
                              }`}
                              title={playingMessageId === message.id && isSpeaking ? 'Stop audio' : 'Play audio'}
                            >
                              {playingMessageId === message.id && isSpeaking ? (
                                <>
                                  <FiVolumeX className="mr-1" />
                                  <span>Stop</span>
                                  <motion.div
                                    className="ml-1 w-1 h-1 bg-neon-cyan rounded-full"
                                    animate={{ scale: [1, 1.5, 1] }}
                                    transition={{ duration: 0.8, repeat: Infinity }}
                                  />
                                </>
                              ) : (
                                <>
                                  <FiVolume2 className="mr-1" />
                                  <span>Play</span>
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
                <div ref={messagesEndRef} />
              </AnimatePresence>
            )}
          </div>
        </div>
        
        <div className="glass-panel rounded-2xl p-4">
          <form onSubmit={handleSendMessage} className="flex items-end gap-3">
            {/* Voice Button */}
            <button
              type="button"
              onClick={() => setShowVoiceModal(true)}
              className="flex-shrink-0 p-3 rounded-full bg-glass-light text-gray-300 hover:bg-neon-cyan/20 hover:text-neon-cyan transition-all duration-300"
              title="Start voice chat"
            >
              <FiMic className="w-5 h-5" />
            </button>
            
            {/* Text Input Container */}
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Share your thoughts with Lyo..."
                rows={1}
                className="w-full bg-transparent border-none text-white placeholder-gray-500 focus:outline-none focus:ring-0 resize-none py-3 px-0"
                style={{ 
                  minHeight: '40px',
                  maxHeight: '200px',
                  overflowY: 'hidden'
                }}
              />
            </div>
            
            {/* Send Button */}
            <button
              type="submit"
              disabled={!input.trim()}
              className={`flex-shrink-0 p-3 rounded-full transition-all duration-300 ${
                input.trim() 
                  ? 'bg-neon-cyan text-space hover:bg-neon-cyan/80' 
                  : 'bg-glass-light text-gray-500'
              }`}
              title="Send message"
            >
              <FiSend className="w-5 h-5" />
            </button>
          </form>
        </div>
      </div>
      
      {/* Voice Chat Modal */}
      <VoiceChatModal
        isOpen={showVoiceModal}
        onClose={() => setShowVoiceModal(false)}
        onVoiceMessage={handleVoiceMessage}
      />
    </motion.div>
  )
}

export default Chat